import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PencilSimple, Trash, Plus, X } from "@phosphor-icons/react";
import { Card, money, errToast } from "@/components/finance/fin";
import { EXPENSE_CATS, FREQS, catLabel } from "./budgetModel";

const BLANK = { name: "", category: "mortgage_rent", amount: "", frequency: "monthly", start_month: "", end_month: "" };
const sel = "h-9 rounded-md border border-input bg-secondary/40 px-2 text-sm";

function RecurringForm({ initial, onDone }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    const body = { ...f, amount: Number(f.amount), start_month: f.start_month || null, end_month: f.end_month || null };
    try {
      if (f.id) await api.put(`/finance/recurring-expenses/${f.id}`, body); else await api.post("/finance/recurring-expenses", body);
      toast.success(f.id ? "Recurring expense updated" : "Added to your recurring expenses"); onDone(true);
    } catch (e) { errToast(e); }
  };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 rounded-lg border border-primary/30 bg-secondary/20 p-3" data-testid="recurring-form">
      <Input placeholder="Name (e.g. Hydro One)" value={f.name} onChange={set("name")} data-testid="recurring-name" className="h-9 bg-secondary/40" />
      <select value={f.category} onChange={set("category")} data-testid="recurring-category" className={sel}>{EXPENSE_CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <Input type="number" step="0.01" placeholder="Amount" value={f.amount} onChange={set("amount")} data-testid="recurring-amount" className="h-9 bg-secondary/40" />
      <select value={f.frequency} onChange={set("frequency")} data-testid="recurring-frequency" className={sel}>{FREQS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <Input type="month" value={f.start_month || ""} onChange={set("start_month")} data-testid="recurring-start" className="h-9 bg-secondary/40" title="Starts (optional)" />
      <Input type="month" value={f.end_month || ""} onChange={set("end_month")} data-testid="recurring-end" className="h-9 bg-secondary/40" title="Ends (optional)" />
      <div className="flex gap-2 sm:col-span-2 lg:col-span-3">
        <Button size="sm" onClick={submit} data-testid="recurring-save-btn">{f.id ? "Save changes" : "Add expense"}</Button>
        <Button size="sm" variant="ghost" onClick={() => onDone(false)} data-testid="recurring-cancel-btn"><X className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function RecurringExpenses({ onChanged }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: items = [] } = useQuery({ queryKey: ["fin-recurring"], queryFn: async () => (await api.get("/finance/recurring-expenses")).data });
  const done = (changed) => { setEditing(null); if (changed) { qc.invalidateQueries({ queryKey: ["fin-recurring"] }); onChanged(); } };
  const del = async (x) => {
    if (!window.confirm(`Remove "${x.name}" from your recurring expenses? Saved months keep their figures.`)) return;
    try { await api.delete(`/finance/recurring-expenses/${x.id}`); done(true); } catch (e) { errToast(e); }
  };
  return (
    <Card title="Recurring expenses" subtitle="Set these up once — they're imported into every month automatically. Saved months keep their own figures." testId="recurring-card"
      right={!editing && <Button size="sm" variant="outline" onClick={() => setEditing(BLANK)} data-testid="recurring-add-btn"><Plus className="h-4 w-4 mr-1" /> Add recurring</Button>}>
      {editing && <div className="mb-3"><RecurringForm initial={editing} onDone={done} /></div>}
      {items.length === 0 && !editing && <p className="text-sm text-muted-foreground" data-testid="recurring-empty">No recurring expenses yet. Add rent, insurance, utilities, phone and more.</p>}
      <div className="divide-y divide-border/60">
        {items.map((x) => (
          <div key={x.id} className="flex flex-wrap items-center gap-2 py-2" data-testid={`recurring-item-${x.id}`}>
            <div className="flex-1 min-w-[160px]"><p className="text-sm font-medium">{x.name}</p><p className="text-[11px] text-muted-foreground">{catLabel(x.category)} · {money(x.amount)} {FREQS.find((f) => f[0] === x.frequency)?.[1].toLowerCase()}{x.start_month ? ` · from ${x.start_month}` : ""}{x.end_month ? ` to ${x.end_month}` : ""}</p></div>
            <p className="text-sm font-bold tabular-nums">{money(x.monthly)}/mo</p>
            <Button size="sm" variant="ghost" onClick={() => setEditing(x)} data-testid={`recurring-edit-${x.id}`}><PencilSimple className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => del(x)} data-testid={`recurring-delete-${x.id}`}><Trash className="h-4 w-4 text-red-400" /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
